import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  CheckCircle,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMAGES = {
  dcMonument: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/t8s41v2p_2.jpg',
  capitol: 'https://images.unsplash.com/photo-1648231150978-27af59c1d3ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxXYXNoaW5ndG9uJTIwREMlMjBDYXBpdG9sJTIwYnVpbGRpbmclMjBuaWdodHxlbnwwfHx8fDE3NzM2ODkxOTB8MA&ixlib=rb-4.1.0&q=85',
  whiteHouse: 'https://images.unsplash.com/photo-1570492903043-3d30df7133d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxXYXNoaW5ndG9uJTIwREMlMjBXaGl0ZSUyMEhvdXNlJTIwZ292ZXJubWVudCUyMGV4ZWN1dGl2ZXxlbnwwfHx8fDE3NzM2OTA5NDB8MA&ixlib=rb-4.1.0&q=85',
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (error) {
      console.error('Contact error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={IMAGES.capitol}
            alt="Washington DC Capitol"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/95 to-[#050505]/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block fade-in">
            Contact Us
          </span>
          <h1 
            data-testid="contact-headline"
            className="text-hero font-serif text-white mb-6 max-w-3xl fade-in-delay-1"
          >
            Get in Touch
          </h1>
          <p className="text-[#A3A3A3] text-lg lg:text-xl max-w-2xl leading-relaxed fade-in-delay-2">
            Have questions or ready to book? We&apos;re here to help with all your 
            luxury transportation needs.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-serif text-white mb-8">
                Contact Information
              </h2>

              <div className="space-y-8 mb-12">
                {/* Phone */}
                <a 
                  href="tel:+12026840534"
                  data-testid="contact-phone-link"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-14 h-14 flex items-center justify-center border border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/10 transition-colors">
                    <Phone className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <span className="text-[#A3A3A3] text-sm block mb-1">Phone</span>
                    <span className="text-white text-xl group-hover:text-[#D4AF37] transition-colors">
                      +1 (202) 684-0534
                    </span>
                  </div>
                </a>

                {/* Email */}
                <a 
                  href="mailto:contact@capitalchauffeurservices.com"
                  data-testid="contact-email-link"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-14 h-14 flex items-center justify-center border border-[#D4AF37]/30 group-hover:bg-[#D4AF37]/10 transition-colors">
                    <Mail className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <span className="text-[#A3A3A3] text-sm block mb-1">Email</span>
                    <span className="text-white text-xl group-hover:text-[#D4AF37] transition-colors">
                      contact@capitalchauffeurservices.com
                    </span>
                  </div>
                </a>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 flex items-center justify-center border border-[#D4AF37]/30">
                    <Clock className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <div>
                    <span className="text-[#A3A3A3] text-sm block mb-1">Hours</span>
                    <span className="text-white text-xl">24/7 Service</span>
                    <p className="text-[#A3A3A3] text-sm mt-1">
                      Available around the clock for your convenience
                    </p>
                  </div>
                </div>
              </div>

              {/* Service Area Map */}
              <div className="mt-12">
                <h3 className="text-xl font-serif text-white mb-6 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  Service Area
                </h3>
                
                {/* Custom SVG Map */}
                <div className="map-container aspect-video relative p-8">
                  <svg viewBox="0 0 400 300" className="w-full h-full">
                    {/* Background */}
                    <rect width="400" height="300" fill="#0A0A0A"/>
                    
                    {/* Grid lines */}
                    <g stroke="#262626" strokeWidth="0.5">
                      {[...Array(10)].map((_, i) => (
                        <line key={`h${i}`} x1="0" y1={i * 30} x2="400" y2={i * 30}/>
                      ))}
                      {[...Array(14)].map((_, i) => (
                        <line key={`v${i}`} x1={i * 30} y1="0" x2={i * 30} y2="300"/>
                      ))}
                    </g>

                    {/* Maryland Shape */}
                    <path 
                      d="M 50 50 L 300 30 L 350 80 L 340 150 L 280 180 L 200 200 L 100 180 L 40 120 Z" 
                      fill="#1a1a1a" 
                      stroke="#D4AF37" 
                      strokeWidth="1"
                      opacity="0.8"
                    />
                    <text x="150" y="100" fill="#A3A3A3" fontSize="14" fontFamily="Manrope">Maryland</text>

                    {/* Virginia Shape */}
                    <path 
                      d="M 30 130 L 100 180 L 200 200 L 280 180 L 300 220 L 250 280 L 100 270 L 20 200 Z" 
                      fill="#1a1a1a" 
                      stroke="#D4AF37" 
                      strokeWidth="1"
                      opacity="0.8"
                    />
                    <text x="120" y="240" fill="#A3A3A3" fontSize="14" fontFamily="Manrope">Virginia</text>

                    {/* DC Area */}
                    <circle cx="200" cy="180" r="25" fill="#D4AF37" opacity="0.2" stroke="#D4AF37" strokeWidth="2"/>
                    <circle cx="200" cy="180" r="8" fill="#D4AF37"/>
                    <text x="230" y="175" fill="#D4AF37" fontSize="12" fontWeight="600" fontFamily="Manrope">Washington DC</text>

                    {/* Airport Markers */}
                    <g className="airports">
                      {/* IAD */}
                      <circle cx="120" cy="160" r="6" fill="#050505" stroke="#D4AF37" strokeWidth="2"/>
                      <text x="90" y="145" fill="#A3A3A3" fontSize="10" fontFamily="Manrope">IAD</text>
                      
                      {/* DCA */}
                      <circle cx="210" cy="200" r="6" fill="#050505" stroke="#D4AF37" strokeWidth="2"/>
                      <text x="220" y="210" fill="#A3A3A3" fontSize="10" fontFamily="Manrope">DCA</text>
                      
                      {/* BWI */}
                      <circle cx="280" cy="120" r="6" fill="#050505" stroke="#D4AF37" strokeWidth="2"/>
                      <text x="290" y="115" fill="#A3A3A3" fontSize="10" fontFamily="Manrope">BWI</text>
                    </g>

                    {/* Connecting Lines */}
                    <g stroke="#D4AF37" strokeWidth="1" strokeDasharray="4,4" opacity="0.5">
                      <line x1="120" y1="160" x2="200" y2="180"/>
                      <line x1="210" y1="200" x2="200" y2="180"/>
                      <line x1="280" y1="120" x2="200" y2="180"/>
                    </g>
                  </svg>

                  {/* Legend */}
                  <div className="absolute bottom-4 left-4 flex items-center gap-4 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[#D4AF37]"></div>
                      <span className="text-[#A3A3A3]">Primary Service Area</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full border border-[#D4AF37] bg-[#050505]"></div>
                      <span className="text-[#A3A3A3]">Airports</span>
                    </div>
                  </div>
                </div>

                {/* Service Areas List */}
                <div className="mt-6 grid grid-cols-3 gap-4">
                  {['Washington DC', 'Northern Virginia', 'Maryland'].map((area) => (
                    <div 
                      key={area} 
                      className="p-4 bg-[#0A0A0A] border border-white/5 text-center"
                    >
                      <span className="text-white text-sm">{area}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="glass p-8 lg:p-10">
                {isSubmitted ? (
                  <div data-testid="contact-success" className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]">
                      <CheckCircle className="w-8 h-8 text-[#D4AF37]" />
                    </div>
                    <h3 className="text-2xl font-serif text-white mb-4">
                      Message Sent
                    </h3>
                    <p className="text-[#A3A3A3] mb-8">
                      Thank you for contacting us. We&apos;ll get back to you shortly.
                    </p>
                    <Button
                      data-testid="send-another-btn"
                      onClick={() => {
                        setIsSubmitted(false);
                        setFormData({ name: '', email: '', phone: '', message: '' });
                      }}
                      className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-6 py-5"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-serif text-white mb-2">
                      Send Us a Message
                    </h2>
                    <p className="text-[#A3A3A3] mb-8">
                      Fill out the form below and we&apos;ll respond promptly.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <Label className="text-white">Name *</Label>
                        <Input
                          data-testid="contact-input-name"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Your full name"
                          className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-white">Email *</Label>
                        <Input
                          data-testid="contact-input-email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="your@email.com"
                          className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-white">Phone</Label>
                        <Input
                          data-testid="contact-input-phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+1 (202) 555-0123"
                          className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-white">Message *</Label>
                        <Textarea
                          data-testid="contact-input-message"
                          value={formData.message}
                          onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                          placeholder="How can we help you?"
                          className="bg-transparent border-white/20 focus:border-[#D4AF37] text-white placeholder:text-white/30 min-h-[150px]"
                        />
                      </div>

                      <Button
                        data-testid="contact-submit-btn"
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none py-6 text-sm font-medium tracking-wider uppercase disabled:opacity-50"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send Message
                          </>
                        )}
                      </Button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* DC Landmarks Section */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
              Our Service Area
            </span>
            <h2 className="text-3xl font-serif text-white mb-4">
              Serving the Nation&apos;s Capital
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="aspect-square overflow-hidden">
              <img
                src={IMAGES.whiteHouse}
                alt="The White House"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-square overflow-hidden">
              <img
                src={IMAGES.capitol}
                alt="US Capitol Building"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="aspect-square overflow-hidden">
              <img
                src={IMAGES.dcMonument}
                alt="Washington Monument"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#050505] relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-section font-serif text-white mb-6">
            Ready to{' '}
            <span className="text-[#D4AF37]">Book Your Ride</span>?
          </h2>
          <p className="text-[#A3A3A3] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Skip the form and book directly. Our online reservation system 
            makes it easy to schedule your luxury transportation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/book">
              <Button 
                data-testid="contact-cta-book-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase"
              >
                Book Online
              </Button>
            </a>
            <a href="tel:+12026840534">
              <Button 
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase"
              >
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
